import Spinner from "@/components/spinner/Spinner";
import {
  useCourseCertificateAttachmentPdf,
  useTeachingAttachmentPdf,
} from "@/hooks/cle-hooks";

import type { SupplementaryDocumentKind } from "./types";
import { useObjectUrl } from "./useObjectUrl";

interface SupplementaryDocumentPreviewProps {
  attachmentId: string;
  evidenceId: string;
  fileName: string;
  kind: SupplementaryDocumentKind;
  userId: string;
}

/**
 * Bilaget vist i dialogen.
 *
 * Eget komponent, fordi Modal foerst monterer sit indhold, naar den aabnes:
 * opslaget sker derfor ved klik og ikke ved sideindlaesning. Et kursusbevis med
 * fem bilag ville ellers hente fem PDF'er, ingen havde bedt om.
 *
 * Hoejden er bundet til vinduet frem for et fast tal: er visningen hoejere end
 * modalen, faar modalen sin egen scrollbar uden om PDF-visningen, og saa ruller
 * hjulet den ydre boks i stedet for dokumentet - man skal klikke ind i PDF'en
 * foerst. Passer den, er det dokumentet, der ruller.
 */
export const SupplementaryDocumentPreview = ({
  attachmentId,
  evidenceId,
  fileName,
  kind,
  userId,
}: SupplementaryDocumentPreviewProps) => {
  // Begge forespoergsler findes altid, men kun den, der passer til beviset, er
  // aktiveret og henter noget.
  const certificatePdf = useCourseCertificateAttachmentPdf(
    { userId, id: evidenceId, attachmentId },
    { enabled: kind === "kursusbevis" },
  );
  const teachingPdf = useTeachingAttachmentPdf(
    { userId, teachingId: evidenceId, attachmentId },
    { enabled: kind === "undervisning" },
  );
  const { data, isLoading, isError } =
    kind === "undervisning" ? teachingPdf : certificatePdf;
  const pdfUrl = useObjectUrl(data);

  return (
    <div
      className="border rounded bg-white d-flex align-items-center justify-content-center m-3"
      style={{
        width: "min(80vw, 900px)",
        height: "calc(100vh - 19rem)",
        minHeight: "18rem",
      }}
    >
      {isLoading && <Spinner label="Henter bilag ..." />}
      {!isLoading && isError && (
        <div className="text-muted">Bilaget kunne ikke hentes.</div>
      )}
      {!isLoading && !isError && pdfUrl && (
        <object
          aria-label={`PDF-visning for ${fileName}`}
          className="w-100 h-100 border rounded"
          data={pdfUrl}
          type="application/pdf"
        >
          <a href={pdfUrl} rel="noreferrer" target="_blank">
            Åbn PDF
          </a>
        </object>
      )}
    </div>
  );
};
